import { ColorTypes, actions, dom, MenuItem } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ColumnSource, RowSource, RowModel, CellSource, CellModel, RequestFilters, ConditionalFormat, CellAction } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, FormulaEditor, FormulaEditorAdapter, VariableSource } from "@reporting/rp-app-host";
import { StatementDefinition, StatementRow } from "@reporting/rp-types/definition";
import { RuleList } from "../../components";
import { EntityAccessType } from "@reporting/rp-types/users";

const PAGE_SIZE = 500;

class RowFormulaEditorAdapter extends FormulaEditorAdapter<FormulaEditor> {

	constructor(
		private origFormula: string,
		private onFormulaSave: (newFormula: string) => void,
		private id_statement: number,
	) {	super() }

	async getVariables(): Promise<VariableSource[]> {
		const statementRows = await rp.ms('definition').get<StatementRow[]>('statementRows', {
			id_statement: this.id_statement,
			fields: ['id_row', 'description']
		});
		return statementRows.map(sr => ({
			variable: `*:${sr.id_row}`,
			description: sr.description,
		}));
	}

	async getContent(): Promise<string> {
		return this.origFormula;
	}

	async save(formula?: string): Promise<void|string> {
		this.onFormulaSave(formula);
	}

	getOptions() {
		return this.createOptions({
			closeAfterSave: true,
			toolbar: true,
			variablesGrid: true
		});
	}
}

export class FinRowsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private editedRow: RowModel;
	private a: number;
	private editable: boolean = true;

	constructor (
		private statement: StatementDefinition,
	) {
		super();
		this.a = statement.accounting;
		this.editable = this.a === rp.accountingProvider.a;
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 5,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}],
			filter: false,
			sorting: false,
			hidden: !this.editable,
		}, {
			data: 'id_row',
			header: '#',
			e: false,
			t: 't',
			w: 80,
		}, {
			data: 'row',
			header: msg('Row'),
			t: 't',
			w: 250,
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			w: 250,
		}, {
			data: 'formula',
			header: msg('Formula'),
			t: 't',
			w: 150,
		}, {
			data: 'property_1',
			header: msg('Attributes'),
			t: 't',
			w: 200,
		}, {
			data: 'indent',
			header: msg('Indentation'),
			t: 'n',
			w: 125,
		}, {
			data: 'number_dec',
			header: msg('Decimal digit count'),
			t: 'n',
			w: 175,
		}, {
			data: 'default_view',
			header: msg('Show'),
			t: 'b',
			w: 80,
		}, {
			data: 'drill_view',
			header: msg('Drill'),
			t: 'b',
			w: 80,
		}, {
			data: 'tree_view',
			header: msg('Tree'),
			t: 'b',
			w: 80,
		}, {
			data: 'percentage',
			header: msg('Percents'),
			t: 'b',
			w: 105,
		}, {
			data: 'agregate',
			header: msg('Aggregated'),
			t: 'b',
			w: 115,
		}, {
			data: 'invert_var',
			header: msg('Invert variances'),
			t: 'b',
			w: 160,
		}, {
			data: 'missing_trans',
			header: msg('Transactions with missing definition'),
			t: 'b',
			w: 290,
		}, {
			data: 'chart_coef',
			header: msg('Chart coefficient'),
			t: 'n',
			w: 160,
		}, {
			data: 'style',
			header: msg('Style'),
			t: 't',
			w: 100,
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 160,
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145,
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		}];
		return this._columns;
	}

	private createCell(sr: StatementRow, col: ColumnSource): Partial<CellSource> | null {
		if (col.hidden) return null;
		switch (col.data as string) {
			case 'controls':
				return this.getRowControls(sr.agregate);
			case 'style':
				if(sr.style) {
					let dv = 'style';
					let cellStyle;
					try {
						cellStyle = JSON.parse(sr.style)?.row[0]?.style;
					} catch (e) {
						console.error('Failed to parse style:', e);
						cellStyle = { color: 'ff0000' };
						dv = '#invalid_style';
					}
					return { v: sr.style, dv: dv, s: cellStyle };
				}
		}
		return null;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const statementRows = await rp.ms('definition').get<StatementRow[]>('statementRows', this.createRequestParams(filters, {
			id_statement: this.statement.id_statement,
			count: PAGE_SIZE,
			accounting: this.a,
		}));
		const rows: RowSource[] = [];
		for (const sr of statementRows) {
			const rs: RowSource = { data: sr };
			for (const col of this._columns) {
				const cell = this.createCell(sr, col);
				if (cell) rs.data[col.data] = cell;
			}
			rows.push(rs);
		}
		return rows;
	}

	getRowControls(aggregate: boolean): Partial<CellSource> {
		const drowpdownItems: MenuItem[] = [
			// { k: 'style', v: msg('Style'), icon: 'image' }, // TODO: add style editor
			{ k: 'dictionary', v: msg('Translation'), icon: 'language' },
			{ k: 'access', v: msg('Access'), icon: 'shield-check' },
		];
		if (aggregate) {
			drowpdownItems.unshift({ k: 'aggregate', v: msg('Aggregation'), icon: 'arrow-down-left-and-arrow-up-right-to-center' });
		}

		const items: CellAction[] = [
			{ action: 'delete', title: msg('Delete'), icon: 'trash', color: ColorTypes.Red },
			{ action: 'duplicate', title: msg('Duplicate'), icon: 'copy', color: ColorTypes.Yellow },
			{ action: 'subtree', title: msg('Tree'), icon: 'list-tree', color: ColorTypes.Accent },
			{ action: 'formula', title: msg('Formula'), icon: 'calculator', color: ColorTypes.Gray },
			{ action: 'dd', icon: 'ellipsis', color: ColorTypes.Gray, type: 'menu', items: drowpdownItems }
		];

		return { content: items };
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			refreshAfterSave: true,
			editable: this.editable,
			pageSize: PAGE_SIZE,
			freezeLeft: 3,
			toolbar: {
				save: { enabled: this.editable },
				discard: { enabled: this.editable },
				new: { enabled: this.editable }
			},
			customToolbar: [{
				type: 'button',
				id: 'aggregatedRows',
				text: msg('Aggregated rows'),
				icon: 'arrow-down-left-and-arrow-up-right-to-center',
				color: ColorTypes.Gray
			}] as ToolbarItem[],
			showEmptyRow: this.editable,
			rowMenu: {
				addRows: this.editable,
				deleteRows: this.editable,
			},
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index, id_statement: this.statement.id_statement}, row.data));
		await rp.ms('definition').post('statementRows', data);
	}

	async onAction(action: string, cell: CellModel, event: actions.ActionEvent) {
		this.editedRow = cell?.row;
		const data = cell?.row?.data as StatementRow;
		switch (action) {
			// toolbar actions
			case 'aggregatedRows':
				rp.host.open(`definition-finAggregatedRows-${this.statement.id_statement}`, { title: `${msg('Aggregated rows')}: ${this.statement.statement}` });
				break;
			// row actions
			case 'formula':
				rp.host.openModalSidebar({
					component: this.getFormulaEditorComponent(data.formula),
					caption: `#${data.id_row} ${data.description || ''}`,
					width: 500,
				})
				break;
			case 'subtree':
				rp.host.open(
					`definition-finTree-${this.statement.id_statement}-0-${data.id_row}`,
					{ title: cell.row.data.row }
				);
				break;
			case 'dd.aggregate':
				rp.host.open(
					`definition-finAggregate-${data.id_row}`,
					{ title: `${msg('Aggregation')} - ${data.row}` }
				);
				break;
			case 'dd.style':
				rp.host.openModalSidebar({
					component: this.getStyleEditorComponent(data.style),
					caption: `${msg('Style')} - #${data.id_row} ${data.description || ''}`
				});
				break;
			case 'dd.dictionary':
				if (data.description) {
					const component = await rp.host.open(`definition-translations-${data.description}`, { title: msg('Translations') });
					component.setAttribute('term', data.description);
				}
				break;
			case 'dd.access':
				rp.host.open(`users-entityUsers-${EntityAccessType.StatementRow}-${data.id_row}`, { title: `${msg('Access')}: ${data.row}` });
				break;
		}
	}

	getFormulaEditorComponent(origFormula: string): HTMLElement {
		const component = dom.createElement<FormulaEditor>('rp-editor-formula');
		component.adapter = new RowFormulaEditorAdapter(origFormula, this.setFormula.bind(this), this.statement.id_statement);
		return component;
	}

	setFormula(newFormula: string) {
		const r = this.editedRow.index;
		const c = this.editedRow.cells.findIndex(cell => cell.column.data == 'formula');
		if (c < 0) return;
		this.editor.setCellValue(r, c, newFormula);
	}

	getStyleEditorComponent(origStyle: string): HTMLElement {
		const component = dom.createElement<RuleList>('rp-definition-rules');
		const conditionalFormats: ConditionalFormat[] = origStyle ? JSON.parse(origStyle) : [];
		conditionalFormats.forEach((format: ConditionalFormat) => { format.type = format.type || 'formula' });
		component.conditionalFormats = conditionalFormats;
		component.onSave = this.setStyle.bind(this);
		return component;
	}

	setStyle(newStyle: ConditionalFormat[]) {
		const r = this.editedRow.index;
		const c = this.editedRow.cells.findIndex(cell => cell.column.data == 'style');
		if (c < 0) return;
		this.editor.setCellValue(r, c, newStyle.length ? JSON.stringify(newStyle) : null);

	}

}
